<template>
  <DialogPortal>
    <DialogOverlay class="modal-backdrop animated" />
    <DialogContent
      class="modal animated"
      :aria-describedby="null"
      v-bind="{ ...useEmitAsProps(emits) }"
      @pointer-down-outside="eventHandler"
      @escape-key-down="eventHandler"
    >
      <div :class="['modal-dialog', props.size && `modal-${props.size}`, props.centered && 'modal-dialog-centered']">
        <div ref="modalContent" class="modal-content">
          <div class="modal-header">
            <DialogTitle as-child>
              <slot name="title" />
              <DialogClose
                id="closeBtn"
                tabindex="-1"
                class="btn-close"
                aria-label="Close"
              />
            </DialogTitle>
          </div>

          <div class="modal-body">
            <slot />
          </div>

          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </DialogContent>
  </DialogPortal>
</template>

<script setup>
import { ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  useEmitAsProps,
} from 'radix-vue'

const modalContent = ref(null)

const emits = defineEmits([
  'escapeKeyDown',
  'pointerDownOutside',
  'focusOutside',
  'interactOutside',
  'dismiss',
  'openAutoFocus',
  'closeAutoFocus',
])

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    description: 'The modal size (width)',
    validator: (val) => ['sm', 'md', 'lg', 'xl'].includes(val),
  },
  static: {
    type: Boolean,
    description: 'No close button, backdrop clicks, Esc key',
    default: false,
  },
  centered: {
    type: Boolean,
    description: 'Center the modal vertically',
    default: false,
  },
})

onClickOutside(modalContent, ({ target }) => {
  if (target?.closest('[data-gu-toaster]')) return
  if (target?.closest('[data-radix-popper-content-wrapper]')) return
  if (props.static) return
  document.getElementById('closeBtn').click()
})

const eventHandler = (event) => {
  if (props.static) event.preventDefault()
}
</script>

<style lang="scss" scoped>
.modal {
  animation-duration: .3s, .15s;
  animation-timing-function: ease-out, linear;
  display: block;

  &[data-state='open'] {
    animation-name: slide-in-from-top, fade-in;
  }

  &[data-state='closed'] {
    animation-name: slide-out-from-top, fade-out;
  }
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  animation-duration: 0.15s;

  &[data-state='open'] {
    animation-name: fade-in;
  }

  &[data-state='closed'] {
    animation-name: fade-out;
  }
}
</style>
